import { createColumnHelper } from '@tanstack/react-table';
import { Edit } from 'lucide-react';

import { Button } from '@@/buttons';
import { Badge } from '@@/Badge';
import { Switch } from '@@/form-components/SwitchField/Switch';

import { AlertRuleRow } from './types';

export const columnHelper = createColumnHelper<AlertRuleRow>();

export function createColumns(
  onEdit: (alertRule: AlertRuleRow) => void,
  onToggle: (alertRule: AlertRuleRow, enabled: boolean) => void,
  togglingId?: number
) {
  return [
    columnHelper.accessor('name', {
      header: 'Alert Rule',
      cell: ({ getValue, row }) => {
        const { isInternal, supportedAgentVersion, supportedEnvironmentTypes } =
          row.original;

        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{getValue()}</span>
            {isInternal && (
              <Badge type="info" className="text-xs">
                System
              </Badge>
            )}
            {supportedEnvironmentTypes && (
              <Badge type="success" className="text-xs">
                {supportedEnvironmentTypes}
              </Badge>
            )}
            {supportedAgentVersion && (
              <Badge type="warn" className="text-xs">
                {supportedAgentVersion}
              </Badge>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('description', {
      header: 'Description',
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
    }),
    columnHelper.accessor('threshold', {
      header: 'Threshold',
      cell: ({ getValue }) => {
        const threshold = getValue();

        // For rules without numeric thresholds (missing threshold field), show dash
        if (threshold === undefined || threshold === null) {
          return '-';
        }

        return threshold;
      },
    }),
    columnHelper.accessor('conditionOperator', {
      header: 'Condition',
      cell: ({ getValue, row }) => {
        const operator = getValue();
        const { threshold } = row.original;

        // If there's no threshold, this rule doesn't use numeric conditions
        if (threshold === undefined || threshold === null) {
          return '-';
        }

        // Show the operator for numeric threshold rules
        return operator || '>';
      },
    }),
    columnHelper.accessor('duration', {
      header: 'Duration (min)',
      cell: ({ getValue }) => {
        const duration = getValue();
        return duration ? `${duration}m` : '-';
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const alertRule = row.original;
        return (
          <div className="flex gap-2 items-center">
            <Switch
              id={`toggle-alert-rule-${alertRule.id}`}
              name={`toggle-alert-rule-${alertRule.id}`}
              checked={alertRule.enabled}
              onChange={() => onToggle(alertRule, !alertRule.enabled)}
              disabled={togglingId === alertRule.id}
              data-cy={`toggle-alert-rule-${alertRule.id}`}
            />
            <Button
              color="primary"
              size="xsmall"
              icon={Edit}
              onClick={() => onEdit(alertRule)}
              disabled={!alertRule.isEditable}
              data-cy={`edit-alert-rule-${alertRule.id}`}
            >
              Edit
            </Button>
          </div>
        );
      },
    }),
  ];
}

// Default columns for backward compatibility
export const columns = createColumns(
  () => {},
  () => {}
);
